import React from 'react';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useCallback } from 'react';
import type { CreateEventHeaderLayoutSettings, CreateEventPayload0301, WebsiteDesignSettingsInput } from '@graphql/generated-base';
import { DesignLayoutType } from '@graphql/generated-base';
import { useAuth } from '@shared/components/AuthProvider';
import { getNodeEnv } from '@shared/utils/getEnvironmentVariables';
import { useToast } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { WarningTriangle } from '@withjoy/joykit/icons';
import { ValidationError } from 'yup';
import { getAlohaCenterHeaderLayout } from '@apps/acquisitionFunnels/createWedding/utils/getAlohaCenterHeaderLayout';

export type DefaultTemplatePayload = Readonly<{
  template: string;
  theme: string;
  websiteDesignSettings: Readonly<WebsiteDesignSettingsInput>;
  headerLayout: Readonly<CreateEventHeaderLayoutSettings>;
}>;

const { isDevelopment } = getNodeEnv();

const formatValidationErrors = (error: unknown): string => {
  if (error instanceof ValidationError && error.errors?.length > 0) {
    return error.errors?.map((err, index) => `${index + 1}. ${err}`).join('\n');
  }

  if (error instanceof Error) {
    return `1. ${error.message}`;
  }

  return '1. Unknown validation error';
};

export const useDefaultTemplatePayload = () => {
  const { payload: defaultTemplatePayload } = useFeatureValue('createWeddingDefaultThemeExperiment');
  const { isSuperAdmin } = useAuth();
  const { toast } = useToast();
  const { tq } = useTranslation('createWedding');

  const applyDefaultTemplatePayload = useCallback(
    async (payload: CreateEventPayload0301) => {
      if (defaultTemplatePayload && (isSuperAdmin || isDevelopment)) {
        //All schema go through testing in development environment. The validation is not added to production due to performance reasons.
        const { websiteDesignSchema } = await import('./util');

        try {
          websiteDesignSchema.validateSync(defaultTemplatePayload, { strict: true, abortEarly: false });
        } catch (error) {
          toast(tq`Invalid template settings payload`, {
            icon: <WarningTriangle />,
            position: 'top-center',
          });
          const formattedErrors = formatValidationErrors(error);
          console.error('Validation errors:\n' + formattedErrors);
          return { payload, error: true };
        }
      }

      // The variant is not control
      if (defaultTemplatePayload) {
        const validatedDefaultTemplatePayload = defaultTemplatePayload as DefaultTemplatePayload;
        return {
          payload: {
            ...payload,
            template: validatedDefaultTemplatePayload.template,
            theme: validatedDefaultTemplatePayload.theme,
            websiteDesignSettings: validatedDefaultTemplatePayload.websiteDesignSettings,
            headerLayout: validatedDefaultTemplatePayload.headerLayout,
          },
          error: false,
        };
      }

      // The variant is control so if the payload does not have a template it defaults to aloha layout with header.
      const shouldApplyAlohaHeader = !payload.template || payload.template === DesignLayoutType.aloha;
      return {
        payload: {
          ...payload,
          ...(shouldApplyAlohaHeader ? { headerLayout: getAlohaCenterHeaderLayout() } : {}),
        },
        error: false,
      };
    },
    [defaultTemplatePayload, isSuperAdmin, tq, toast],
  );

  return applyDefaultTemplatePayload;
};
